import { Component } from 'react';
import PropTypes from 'prop-types';

/**
 * Error Boundary component to catch and handle React errors gracefully
 * Provides fallback UI when JavaScript errors occur in component tree
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    this.setState({
      error,
      errorInfo,
    });

    // Log to error reporting service in production
    if (import.meta.env.PROD) {
      console.error('Error Boundary caught an error:', error, errorInfo);
      // Here you could send error to monitoring service like Sentry
      // Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    }
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-secondary-secondary-05 px-4">
          <div className="max-w-md text-center">
            <h1 className="mb-4 text-2xl font-bold text-primary-primary-50">
              Oops! Something went wrong
            </h1>
            <p className="mb-6 text-neutral-neutral-70">
              We&apos;re sorry for the inconvenience. Please try refreshing the
              page or contact us if the problem persists.
            </p>
            <div className="space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="rounded bg-primary-primary-50 px-4 py-2 text-white transition-colors hover:bg-primary-primary-40"
              >
                Refresh Page
              </button>
              <button
                onClick={() =>
                  (window.location.href = 'mailto:contact@furnitech.app')
                }
                className="rounded border border-primary-primary-50 px-4 py-2 text-primary-primary-50 transition-colors hover:bg-primary-primary-50 hover:text-white"
              >
                Contact Support
              </button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-600">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs">
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
